import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Subscription, calculateTrialDaysRemaining } from '@/lib/billing-utils';

export function useSubscription(companyId: string | undefined) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    fetchSubscription();
    
    // Set up real-time subscription listener
    const channel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId]);

  const fetchSubscription = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No subscription found - create a default trial subscription
          await createDefaultTrialSubscription(companyId);
          return;
        }
        throw fetchError;
      }

      setSubscription(data as Subscription);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const createDefaultTrialSubscription = async (companyId: string) => {
    try {
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 30); // 30-day trial

      const { data, error: createError } = await supabase
        .from('subscriptions')
        .insert({
          company_id: companyId,
          plan_type: 'trial',
          trial_ends_at: trialEndsAt.toISOString(),
          paid_seats: 0,
          seat_price_per_month: 20.00,
          status: 'active',
        })
        .select()
        .single();

      if (createError) throw createError;
      
      setSubscription(data as Subscription);
    } catch (err) {
      console.error('Error creating default subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to create subscription');
    }
  };

  const trialDaysRemaining = subscription?.trial_ends_at 
    ? calculateTrialDaysRemaining(subscription.trial_ends_at)
    : 0;

  return {
    subscription,
    loading,
    error,
    trialDaysRemaining,
    refetch: fetchSubscription,
  };
}
